import React, { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, X, FileText, DollarSign, Clock, Mail, Upload,
  CheckCircle2, Eye, ShieldCheck, Send, StickyNote,
  Briefcase, TrendingUp, Wallet, AlertTriangle,
  ToggleLeft, ToggleRight, CircleCheck, Circle, XCircle,
  ArrowRight, Users, CalendarDays, ExternalLink
} from "lucide-react";
import { fmtUSD } from "../../utils/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact360Drawer({ open, onClose, userId, userType, onViewDeal, onEntityNavigate }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [draftingEmail, setDraftingEmail] = useState(false);
  const [draftResult, setDraftResult] = useState(null);
  const [draftContext, setDraftContext] = useState("follow_up");
  const [verifying, setVerifying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState("general");
  const fileInputRef = useRef(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!userId || !open) return;
    setLoading(true);
    setDraftResult(null);
    try {
      const entityType = userType === "borrower" ? "borrower" : userType === "lender" ? "lender" : "investor";
      const [mainRes, notesRes, onbRes] = await Promise.all([
        axios.get(`${API}/entity/${entityType}/${userId}/360`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/entity/notes/${entityType}/${userId}`, { headers }).catch(() => ({ data: { notes: [] } })),
        axios.get(`${API}/admin/users/${userId}/onboarding-status`, { headers }).catch(() => ({ data: null })),
      ]);
      setData(mainRes.data);
      setNotes(notesRes.data?.notes || []);
      setOnboarding(onbRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [userId, open, userType]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (open) setActiveTab("overview"); }, [userId, open]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const entityType = userType === "borrower" ? "borrower" : userType === "lender" ? "lender" : "investor";
      await axios.post(`${API}/entity/notes/${entityType}/${userId}`, { content: newNote, mentions: [] }, { headers });
      setNewNote("");
      const notesRes = await axios.get(`${API}/entity/notes/${entityType}/${userId}`, { headers }).catch(() => ({ data: { notes: [] } }));
      setNotes(notesRes.data?.notes || []);
    } catch (err) { console.error(err); }
    finally { setSavingNote(false); }
  };

  const handleDraftEmail = async () => {
    setDraftingEmail(true);
    setDraftResult(null);
    try {
      const res = await axios.post(`${API}/ai-success/draft`, {
        user_id: userId, user_type: userType, context_type: draftContext,
      }, { headers });
      setDraftResult({ subject: res.data.subject, body: res.data.body_html || res.data.body, draft_id: res.data.draft_id });
    } catch (err) {
      setDraftResult({ error: "Failed to generate draft. Try again." });
    }
    finally { setDraftingEmail(false); }
  };

  const handleSendDraft = async () => {
    if (!draftResult?.draft_id) return;
    try {
      await axios.post(`${API}/ai-success/approve/${draftResult.draft_id}`, {}, { headers });
      setDraftResult({ ...draftResult, sent: true });
    } catch (err) { console.error(err); }
  };

  const handleVerifyToggle = async () => {
    if (!onboarding) return;
    const action = onboarding.is_verified ? "unverify" : "verify";
    if (action === "verify" && !onboarding.ready_for_verification) {
      const missing = [];
      if (!onboarding.profile_complete) missing.push("Complete profile");
      if (!onboarding.docs_complete) {
        const missingDocs = (onboarding.doc_checklist || []).filter(d => !d.uploaded).map(d => d.label);
        missing.push(`Upload: ${missingDocs.join(", ")}`);
      }
      alert(`Cannot verify yet:\n${missing.join("\n")}`);
      return;
    }
    if (!window.confirm(`${action === "verify" ? "Verify" : "Remove verification for"} this ${userType}?`)) return;
    setVerifying(true);
    try {
      await axios.put(`${API}/admin/users/${userId}/verify`, { action }, { headers });
      const onbRes = await axios.get(`${API}/admin/users/${userId}/onboarding-status`, { headers });
      setOnboarding(onbRes.data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Verification update failed";
      alert(msg);
    }
    finally { setVerifying(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(
        `${API}/documents/profile/upload?target_user_id=${userId}&doc_type=${uploadType}&notes=Uploaded by admin`,
        formData,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      // Refresh data
      fetchData();
    } catch (err) {
      console.error(err);
      alert("Upload failed: " + (err.response?.data?.detail || err.message));
    }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  if (!open) return null;

  const profile = data?.profile || {};
  const fullName = profile.full_name || "Unknown";
  const email = profile.email || "";
  const role = profile.role || userType;
  const phone = profile.phone || "";
  const company = profile.company || "";
  const createdAt = profile.created_at;
  const status = profile.status || "active";

  const stats = data?.stats || {};
  const deals = data?.deals || data?.deals_reviewed || [];
  const documents = data?.documents || [];
  const timeline = data?.timeline || data?.alerts || [];
  const commitments = data?.commitments || [];
  const distributions = data?.distributions || [];
  const wallet = data?.wallet || null;
  const termSheets = data?.term_sheets || [];
  const fundedDeals = data?.funded_deals || [];
  const loanBoxes = data?.loan_boxes || [];

  const verifyLabel = userType === "investor" ? "Accredited" : "Verified";
  const isVerified = onboarding?.is_verified || false;

  const TABS = [
    { id: "overview", label: "Overview", icon: Eye },
    { id: "activity", label: userType === "investor" ? "Investments" : userType === "borrower" ? "Deals" : "Activity", icon: DollarSign },
    { id: "docs", label: "Documents", icon: FileText },
    { id: "notes", label: "Notes", icon: StickyNote },
    { id: "email", label: "Email", icon: Mail },
  ];

  // Onboarding progress
  const profileComplete = onboarding?.profile_complete || false;
  const docsComplete = onboarding?.docs_complete || false;
  const steps = [
    { label: "Profile", done: profileComplete },
    { label: "Documents", done: docsComplete },
    { label: verifyLabel, done: isVerified },
  ];
  const completedSteps = steps.filter(s => s.done).length;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.3 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-40" onClick={onClose} />
          <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-screen w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col"
            data-testid="contact-360-drawer">

            {/* Header */}
            <div className="p-5 border-b border-[#e8e0d0] bg-[#faf7f2] shrink-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <h2 className="text-base font-bold text-[#0A0A0A] truncate" data-testid="c360-name">{fullName}</h2>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {email && <span className="text-[10px] text-[#888]">{email}</span>}
                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase border ${
                      role === "borrower" ? "bg-blue-100 text-blue-700 border-blue-200" :
                      role === "lender" ? "bg-purple-100 text-purple-700 border-purple-200" :
                      "bg-amber-100 text-amber-700 border-amber-200"
                    }`}>{role}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${
                      status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
                    }`}>{status}</span>
                    <a href={`/entity/${role}/${userId}`} target="_blank" rel="noopener noreferrer"
                      className="text-[10px] text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-0.5"
                      data-testid="c360-view-full-profile">
                      <ExternalLink className="h-3 w-3" /> Full Profile
                    </a>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#e8e0d0] transition-colors ml-2 relative z-10" data-testid="c360-close">
                  <X className="h-4 w-4 text-[#888]" />
                </button>
              </div>

              {/* Onboarding Progress Bar */}
              {onboarding && (
                <div className="mt-3 space-y-2" data-testid="c360-onboarding">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] uppercase tracking-wider text-[#999] font-semibold">Onboarding Progress</span>
                    <span className="text-[9px] text-[#666] font-semibold">{completedSteps}/{steps.length}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {steps.map((step, i) => (
                      <div key={step.label} className="flex items-center gap-1 flex-1">
                        <div className={`h-1.5 rounded-full flex-1 transition-all ${step.done ? "bg-emerald-500" : "bg-[#e8e0d0]"}`} />
                        {i < steps.length - 1 && <div className="w-0.5" />}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    {steps.map(step => (
                      <div key={step.label} className="flex items-center gap-1">
                        {step.done ? <CircleCheck className="h-3 w-3 text-emerald-500" /> : <Circle className="h-3 w-3 text-[#ccc]" />}
                        <span className={`text-[9px] ${step.done ? "text-emerald-700 font-semibold" : "text-[#999]"}`}>{step.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Verification Toggle */}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[10px] font-semibold text-[#555]">{verifyLabel} Status</span>
                    <button onClick={handleVerifyToggle} disabled={verifying}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold transition-all ${
                        isVerified
                          ? "bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                          : "bg-[#f5f0e8] text-[#888] border border-[#e8e0d0] hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200"
                      }`}
                      data-testid="c360-verify-toggle">
                      {verifying ? <Loader2 className="h-3 w-3 animate-spin" /> : (
                        isVerified ? <ToggleRight className="h-3.5 w-3.5" /> : <ToggleLeft className="h-3.5 w-3.5" />
                      )}
                      {isVerified ? verifyLabel : `Not ${verifyLabel}`}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Tab Bar */}
            <div className="flex items-center gap-0.5 px-5 pt-3 pb-0 overflow-x-auto shrink-0">
              {TABS.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`c360-tab-${tab.id}`}
                  className={`flex items-center gap-1 px-3 py-2 rounded-t-lg text-[11px] font-semibold transition-all border-b-2 ${
                    activeTab === tab.id ? "text-emerald-700 border-emerald-600 bg-white" : "text-[#888] border-transparent hover:text-[#555]"
                  }`}>
                  <tab.icon className="h-3 w-3" /> {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />
                </div>
              ) : !data ? (
                <div className="text-center py-20 text-sm text-[#999]" data-testid="c360-no-data">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-[#ccc]" />
                  Unable to load profile data
                </div>
              ) : (
                <>
                  {/* ── OVERVIEW TAB ── */}
                  {activeTab === "overview" && <OverviewTab
                    userType={userType} stats={stats} profile={profile} wallet={wallet}
                    timeline={timeline} fullName={fullName} email={email} phone={phone}
                    company={company} createdAt={createdAt} loanBoxes={loanBoxes} />}

                  {/* ── ACTIVITY TAB ── */}
                  {activeTab === "activity" && <ActivityTab
                    userType={userType} deals={deals} termSheets={termSheets}
                    fundedDeals={fundedDeals} commitments={commitments}
                    distributions={distributions} onViewDeal={onViewDeal}
                    onEntityNavigate={onEntityNavigate} />}

                  {/* ── DOCUMENTS TAB ── */}
                  {activeTab === "docs" && <DocumentsTab
                    documents={documents} onboarding={onboarding} uploading={uploading}
                    uploadType={uploadType} setUploadType={setUploadType}
                    fileInputRef={fileInputRef} handleFileUpload={handleFileUpload}
                    userType={userType} userId={userId} onRefresh={fetchData} />}

                  {/* ── NOTES TAB ── */}
                  {activeTab === "notes" && (
                    <div className="space-y-3" data-testid="c360-notes">
                      <div className="flex gap-2">
                        <input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..."
                          className="flex-1 px-3 py-2 rounded-xl border border-[#e8e0d0] text-xs focus:outline-none focus:border-emerald-400"
                          onKeyDown={e => e.key === "Enter" && handleAddNote()} data-testid="c360-note-input" />
                        <button onClick={handleAddNote} disabled={savingNote || !newNote.trim()}
                          className="px-3 py-2 rounded-xl bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50"
                          data-testid="c360-note-add">
                          {savingNote ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add"}
                        </button>
                      </div>
                      {notes.length === 0 ? <EmptyState text="No notes yet" /> : notes.map((note, i) => (
                        <div key={note.note_id || i} className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-3">
                          <div className="text-xs text-[#333]">{note.content}</div>
                          <div className="text-[10px] text-[#888] mt-1">
                            {note.author_name || note.created_by_name || ""} — {note.created_at ? new Date(note.created_at).toLocaleString() : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── EMAIL TAB ── */}
                  {activeTab === "email" && (
                    <div className="space-y-4" data-testid="c360-email">
                      <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-[#999] mb-3">AI Draft Email</h4>
                        <select value={draftContext} onChange={e => setDraftContext(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl border border-[#e8e0d0] text-xs mb-3 focus:outline-none"
                          data-testid="c360-email-context">
                          <option value="follow_up">General Follow-Up</option>
                          <option value="missing_docs">Missing Documents</option>
                          <option value="stale_deal">Stale Deal Nudge</option>
                          <option value="re_engagement">Re-Engagement</option>
                          <option value="welcome">Welcome Email</option>
                        </select>
                        <button onClick={handleDraftEmail} disabled={draftingEmail}
                          className="w-full px-4 py-2.5 rounded-xl bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                          data-testid="c360-email-draft-btn">
                          {draftingEmail ? <><Loader2 className="h-3 w-3 animate-spin" /> Generating...</> : <><Send className="h-3 w-3" /> Generate AI Draft</>}
                        </button>
                      </div>

                      {/* Draft Preview — Editable */}
                      {draftResult && !draftResult.error && (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-4 space-y-3" data-testid="c360-email-preview">
                          {draftResult.sent ? (
                            <div className="text-center py-4">
                              <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                              <div className="text-sm font-semibold text-emerald-700">Email approved and queued for sending</div>
                            </div>
                          ) : (
                            <>
                              <div>
                                <label className="text-[10px] uppercase tracking-wider text-[#999] mb-1 block">Subject</label>
                                <input value={draftResult.subject || ""}
                                  onChange={e => setDraftResult(prev => ({ ...prev, subject: e.target.value }))}
                                  className="w-full px-3 py-2 rounded-lg border border-[#e0d8c8] bg-white text-xs font-semibold text-[#333] focus:outline-none focus:border-emerald-400"
                                  data-testid="c360-email-subject-edit" />
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-wider text-[#999] mb-1 block">Body</label>
                                <textarea value={draftResult.bodyText || draftResult.body || ""}
                                  onChange={e => setDraftResult(prev => ({ ...prev, bodyText: e.target.value, body: e.target.value }))}
                                  className="w-full px-3 py-2 rounded-lg border border-[#e0d8c8] bg-white text-xs text-[#555] leading-relaxed focus:outline-none focus:border-emerald-400 min-h-[120px] resize-y"
                                  data-testid="c360-email-body-edit" />
                              </div>
                              <div className="flex items-center gap-2">
                                <button onClick={handleSendDraft}
                                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors"
                                  data-testid="c360-email-send-btn">
                                  <Send className="h-3 w-3" /> Approve & Send
                                </button>
                                <button onClick={() => setDraftResult(null)}
                                  className="px-4 py-2 rounded-xl border border-[#e8e0d0] text-xs text-[#666] hover:bg-[#faf7f2] transition-colors">
                                  Discard
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {draftResult?.error && (
                        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-600">{draftResult.error}</div>
                      )}

                      {/* Sent Email History */}
                      <EmailHistory userId={userId} headers={headers} refreshKey={draftResult?.sent ? Date.now() : 0} />
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ════════════════════════════════════════════════════════
// ── OVERVIEW TAB
// ════════════════════════════════════════════════════════
function OverviewTab({ userType, stats, profile, wallet, timeline, fullName, email, phone, company, createdAt, loanBoxes }) {
  return (
    <div className="space-y-4" data-testid="c360-overview">
      {/* Key Metrics */}
      <div className="grid grid-cols-3 gap-3">
        {userType === "borrower" && (
          <>
            <MetricCard label="Deals" value={stats.total_deals || 0} />
            <MetricCard label="Funded" value={stats.funded_deals || 0} color="text-emerald-600" />
            <MetricCard label="Volume" value={fmtUSD(stats.total_volume || 0)} color="text-[#b89d5a]" />
          </>
        )}
        {userType === "investor" && (
          <>
            <MetricCard label="Committed" value={fmtUSD(stats.total_committed || 0)} color="text-[#b89d5a]" />
            <MetricCard label="Investments" value={stats.active_investments || 0} />
            <MetricCard label="Distributed" value={fmtUSD(stats.total_distributed || 0)} color="text-emerald-600" />
          </>
        )}
        {userType === "lender" && (
          <>
            <MetricCard label="Reviewed" value={stats.deals_reviewed || 0} />
            <MetricCard label="Term Sheets" value={stats.term_sheets_submitted || 0} />
            <MetricCard label="Funded" value={stats.deals_funded || 0} color="text-emerald-600" />
          </>
        )}
      </div>

      {/* Investor Wallet */}
      {userType === "investor" && wallet && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-[#999] mb-2 flex items-center gap-1">
            <Wallet className="h-3 w-3" /> Wallet
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "Available", value: fmtUSD(wallet.available || 0) },
              { label: "Committed", value: fmtUSD(wallet.committed || 0) },
              { label: "Total Funded", value: fmtUSD(wallet.total_funded || 0) },
              { label: "Distributed", value: fmtUSD(wallet.total_distributed || 0) },
            ].map(w => (
              <div key={w.label} className="py-1">
                <span className="text-[9px] text-[#888] uppercase">{w.label}</span>
                <div className="text-xs font-semibold text-[#333]">{w.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lender Loan Boxes */}
      {userType === "lender" && loanBoxes && loanBoxes.length > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-[#999] mb-2 flex items-center gap-1">
            <Briefcase className="h-3 w-3" /> Loan Boxes ({loanBoxes.length})
          </h4>
          {loanBoxes.map((lb, i) => (
            <div key={lb.box_id || i} className="py-2 border-b border-[#f0ebe0] last:border-0">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#333]">{lb.name || `Loan Box ${i + 1}`}</span>
                <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${lb.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                  {lb.active ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="text-[10px] text-[#888] mt-0.5">
                {lb.loan_types?.join(", ") || "All types"} | {fmtUSD(lb.min_loan || 0)} - {fmtUSD(lb.max_loan || 0)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Profile Info */}
      <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 space-y-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-[#999]">Profile</h4>
        {[
          { label: "Name", value: fullName },
          { label: "Email", value: email || "—" },
          { label: "Phone", value: phone || "—" },
          { label: "Company", value: company || "—" },
          { label: "Joined", value: createdAt ? new Date(createdAt).toLocaleDateString() : "—" },
        ].map(f => (
          <div key={f.label} className="flex items-center justify-between py-1 border-b border-[#f0ebe0] last:border-0">
            <span className="text-[10px] text-[#888] uppercase tracking-wider">{f.label}</span>
            <span className="text-xs font-medium text-[#333] truncate max-w-[60%] text-right">{f.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// ── ACTIVITY TAB — Rich Deal 360 Cards
// ════════════════════════════════════════════════════════
function ActivityTab({ userType, deals, termSheets, fundedDeals, commitments, distributions, onViewDeal, onEntityNavigate }) {
  return (
    <div className="space-y-4" data-testid="c360-activity">
      {userType === "borrower" && (
        deals.length === 0 ? <EmptyState text="No deals found" /> : (
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-[#999]">Deals ({deals.length})</h4>
            {deals.map(d => <DealCard360 key={d.lead_id} deal={d} onViewDeal={onViewDeal} onEntityNavigate={onEntityNavigate} />)}
          </div>
        )
      )}

      {userType === "lender" && (
        <>
          {/* Term Sheets — Full Details */}
          {termSheets.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#999]">Term Sheets ({termSheets.length})</h4>
              {termSheets.map((ts, i) => <TermSheetCard key={ts.term_sheet_id || i} ts={ts} onViewDeal={onViewDeal} onEntityNavigate={onEntityNavigate} />)}
            </div>
          )}

          {/* Deals Reviewed */}
          {deals.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#999]">Deals Reviewed ({deals.length})</h4>
              {deals.map(d => <DealCard360 key={d.lead_id} deal={d} onViewDeal={onViewDeal} onEntityNavigate={onEntityNavigate} />)}
            </div>
          )}

          {/* Funded Deals */}
          {fundedDeals.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#999]">Funded Deals ({fundedDeals.length})</h4>
              {fundedDeals.map(d => <DealCard360 key={d.lead_id} deal={d} onViewDeal={onViewDeal} onEntityNavigate={onEntityNavigate} />)}
            </div>
          )}

          {termSheets.length === 0 && deals.length === 0 && fundedDeals.length === 0 && (
            <EmptyState text="No lending activity found" />
          )}
        </>
      )}

      {userType === "investor" && (
        <>
          {commitments.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#999]">Commitments ({commitments.length})</h4>
              {commitments.map(c => <CommitmentCard key={c.commitment_id} c={c} onViewDeal={onViewDeal} onEntityNavigate={onEntityNavigate} />)}
            </div>
          )}

          {distributions.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#999]">Distributions ({distributions.length})</h4>
              {distributions.map((dist, i) => (
                <div key={dist.distribution_id || i} className="rounded-xl border border-[#e8e0d0] bg-white p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-emerald-600">{fmtUSD(dist.amount || 0)}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                      dist.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                    }`}>{dist.status}</span>
                  </div>
                  <div className="text-[10px] text-[#888] mt-1">{dist.type || "Distribution"} — {dist.created_at ? new Date(dist.created_at).toLocaleDateString() : ""}</div>
                </div>
              ))}
            </div>
          )}

          {commitments.length === 0 && distributions.length === 0 && (
            <EmptyState text="No investment activity found" />
          )}
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════
// ── DOCUMENTS TAB — Upload + Required Docs Checklist
// ════════════════════════════════════════════════════════
function DocumentsTab({ documents, onboarding, uploading, uploadType, setUploadType, fileInputRef, handleFileUpload, userType, userId, onRefresh }) {
  const docChecklist = onboarding?.doc_checklist || [];
  const [reviewingDoc, setReviewingDoc] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewAction, setReviewAction] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const token = localStorage.getItem("ic_token");

  // Get profile docs (verification docs) — these have extra.is_profile_doc
  const profileDocs = documents.filter(d => d.is_profile_doc || d.extra?.is_profile_doc);
  const dealDocs = documents.filter(d => !d.is_profile_doc && !d.extra?.is_profile_doc);

  // Map: doc_type -> doc record (latest by created_at)
  const docByType = {};
  for (const d of profileDocs) {
    const dtype = d.doc_type || d.extra?.doc_type;
    if (dtype) {
      if (!docByType[dtype] || (d.created_at > (docByType[dtype].created_at || ""))) {
        docByType[dtype] = d;
      }
    }
  }

  const handleReview = async (docId, action) => {
    setReviewAction(action);
    try {
      await axios.post(`${API}/documents/profile/review/${docId}`, { action, notes: reviewNotes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviewingDoc(null);
      setReviewNotes("");
      setReviewAction(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Review failed:", err);
      alert(err?.response?.data?.detail || "Review action failed");
      setReviewAction(null);
    }
  };

  const handleViewDoc = async (doc) => {
    const docId = doc.doc_id;
    try {
      const res = await axios.get(`${API}/documents/signed-url/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.signed_url) {
        window.open(res.data.signed_url, "_blank");
      }
    } catch {
      alert("Failed to load document preview");
    }
  };

  const getReviewStatus = (doc) => {
    const extra = doc.extra || {};
    return extra.review_status || "pending_review";
  };

  const statusConfig = {
    approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    rejected: { label: "Rejected", cls: "bg-red-100 text-red-700 border-red-200" },
    pending_review: { label: "Pending Review", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  };

  return (
    <div className="space-y-4" data-testid="c360-docs">
      {/* Verification Document Review Panel */}
      {docChecklist.length > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-[#999]">Verification Documents</h4>
            <ShieldCheck className="h-4 w-4 text-[#b89d5a]" />
          </div>
          <div className="space-y-3">
            {docChecklist.map(req => {
              const doc = docByType[req.type];
              const status = doc ? getReviewStatus(doc) : null;
              const stCfg = status ? (statusConfig[status] || statusConfig.pending_review) : null;
              const isReviewing = reviewingDoc === doc?.doc_id;

              return (
                <div key={req.type} className={`rounded-xl border p-3 transition-all ${
                  !doc ? "border-red-200 bg-red-50/30" : status === "approved" ? "border-emerald-200 bg-emerald-50/30" : status === "rejected" ? "border-red-200 bg-red-50/30" : "border-blue-200 bg-blue-50/30"
                }`} data-testid={`c360-vdoc-${req.type}`}>
                  {/* Header row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      {!doc ? <XCircle className="h-4 w-4 text-red-400 shrink-0" />
                        : status === "approved" ? <CircleCheck className="h-4 w-4 text-emerald-500 shrink-0" />
                        : status === "rejected" ? <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                        : <Clock className="h-4 w-4 text-blue-500 shrink-0" />}
                      <div className="min-w-0">
                        <span className="text-xs font-medium text-[#333] block">{req.label}</span>
                        {doc && <span className="text-[9px] text-[#999]">{doc.original_filename} — {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ""}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {stCfg && <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${stCfg.cls}`}>{stCfg.label}</span>}
                      {!doc && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600 border border-red-200">Not Uploaded</span>}
                    </div>
                  </div>

                  {/* Action buttons for uploaded docs */}
                  {doc && (
                    <div className="mt-2 flex items-center gap-2">
                      <button onClick={() => handleViewDoc(doc)} data-testid={`view-vdoc-${req.type}`}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-[#022c22] text-white hover:bg-[#034d3a] transition-colors">
                        <Eye className="h-3 w-3" /> View
                      </button>
                      {status !== "approved" && (
                        <button onClick={() => handleReview(doc.doc_id, "approve")} disabled={reviewAction === "approve"}
                          data-testid={`approve-vdoc-${req.type}`}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50">
                          {reviewAction === "approve" ? <Loader2 className="h-3 w-3 animate-spin" /> : <CircleCheck className="h-3 w-3" />} Approve
                        </button>
                      )}
                      {status !== "rejected" && status !== "approved" && (
                        <button onClick={() => setReviewingDoc(isReviewing ? null : doc.doc_id)}
                          data-testid={`reject-toggle-${req.type}`}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-red-100 text-red-700 hover:bg-red-200 transition-colors border border-red-200">
                          <XCircle className="h-3 w-3" /> Reject
                        </button>
                      )}
                      {status === "approved" && (
                        <span className="text-[10px] text-emerald-600 font-medium ml-1">
                          {doc.extra?.reviewed_by ? `by ${doc.extra.reviewed_by}` : ""} {doc.extra?.reviewed_at ? `on ${new Date(doc.extra.reviewed_at).toLocaleDateString()}` : ""}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Rejection notes input */}
                  {isReviewing && (
                    <div className="mt-2 space-y-2">
                      <input value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                        placeholder="Reason for rejection (optional)..."
                        className="w-full px-3 py-2 rounded-lg border border-red-200 text-xs focus:outline-none focus:border-red-400"
                        data-testid={`reject-notes-${req.type}`} />
                      <div className="flex gap-2">
                        <button onClick={() => handleReview(doc.doc_id, "reject")} disabled={reviewAction === "reject"}
                          data-testid={`confirm-reject-${req.type}`}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                          {reviewAction === "reject" ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />} Confirm Rejection
                        </button>
                        <button onClick={() => { setReviewingDoc(null); setReviewNotes(""); }}
                          className="px-3 py-1.5 rounded-lg text-[10px] font-medium text-[#666] hover:bg-[#f5f0e8]">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload Section */}
      <div className="rounded-xl border border-dashed border-[#d0c8b0] bg-[#faf7f2] p-4">
        <h4 className="text-xs font-bold uppercase tracking-wider text-[#999] mb-3">Upload Document</h4>
        <select value={uploadType} onChange={e => setUploadType(e.target.value)}
          className="w-full px-3 py-2 rounded-xl border border-[#e8e0d0] text-xs mb-3 focus:outline-none bg-white"
          data-testid="c360-doc-type-select">
          <option value="general">General</option>
          {userType === "borrower" && (
            <>
              <option value="government_id">Government ID (License / Passport)</option>
              <option value="entity_docs">Company / Entity Documents</option>
              <option value="borrower_application">Borrower Application</option>
              <option value="platform_agreement">Signed Platform Agreement</option>
            </>
          )}
          {userType === "lender" && (
            <>
              <option value="business_license">Business License</option>
              <option value="lending_authority">Lending Authority / Charter</option>
              <option value="entity_docs">Entity Documents</option>
              <option value="w9">W-9 Form</option>
            </>
          )}
          {userType === "investor" && (
            <>
              <option value="accreditation_letter">Accreditation Letter</option>
              <option value="government_id">Government ID</option>
              <option value="entity_docs">Entity Documents</option>
              <option value="subscription_agreement">Subscription Agreement</option>
            </>
          )}
          <option value="tax_form">Tax Form</option>
          <option value="compliance">Compliance Document</option>
          <option value="correspondence">Correspondence</option>
        </select>
        <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" data-testid="c360-doc-file-input" />
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[#e8e0d0] bg-white text-xs font-semibold text-[#555] hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition-all disabled:opacity-50"
          data-testid="c360-doc-upload-btn">
          {uploading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading...</> : <><Upload className="h-3.5 w-3.5" /> Choose File & Upload</>}
        </button>
      </div>

      {/* Other Uploaded Documents (non-verification deal docs) */}
      {dealDocs.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-bold uppercase tracking-wider text-[#999]">Deal Documents ({dealDocs.length})</h4>
          {dealDocs.map((doc, i) => (
            <div key={doc.doc_id || i} className="rounded-xl border border-[#e8e0d0] bg-white p-3 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-4 w-4 text-[#b89d5a] shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs font-medium text-[#0A0A0A] truncate">{doc.original_filename || doc.doc_type || "Document"}</div>
                  <div className="text-[10px] text-[#999]">
                    {doc.doc_type && <span className="capitalize">{doc.doc_type.replace(/_/g, " ")}</span>}
                    {doc.created_at && <> — {new Date(doc.created_at).toLocaleDateString()}</>}
                  </div>
                </div>
              </div>
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════
// ── CARD COMPONENTS
// ════════════════════════════════════════════════════════

const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial" };
const STAGE_COLORS = { funded: "bg-emerald-100 text-emerald-700", approved: "bg-emerald-50 text-emerald-600", underwriting: "bg-purple-100 text-purple-700", in_review: "bg-amber-100 text-amber-700", submitted: "bg-blue-100 text-blue-700", declined: "bg-red-100 text-red-700", selected: "bg-emerald-100 text-emerald-700" };

const PIPELINE_STAGES = ["submitted", "in_review", "underwriting", "approved", "funded"];

function DealCard360({ deal, onViewDeal, onEntityNavigate }) {
  const d = deal;
  const loanAmt = d.requested_loan_amount || d.loan_amount || d.purchase_price || 0;
  const ltv = d.purchase_price > 0 ? ((d.requested_loan_amount || 0) / d.purchase_price * 100).toFixed(0) : null;
  const tsCount = d.term_sheet_count || d.term_sheets || 0;
  const intCount = d.interests || 0;
  const createdDate = d.created_at ? new Date(d.created_at) : null;
  const isDeclined = d.stage === "declined";
  const currentStageIdx = PIPELINE_STAGES.indexOf(d.stage);
  const daysSinceCreated = createdDate ? Math.floor((Date.now() - createdDate.getTime()) / 86400000) : null;

  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white hover:border-emerald-300 transition-all hover:shadow-md group cursor-pointer"
      onClick={() => onViewDeal?.(d.lead_id)} data-testid={`c360-deal-${d.lead_id}`}>

      {/* Card Header */}
      <div className="p-4 pb-0">
        <div className="flex items-start justify-between mb-1">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-[#0A0A0A]">{d.deal_number || d.lead_id?.slice(0, 8)}</span>
              {d.loan_type && (
                <span className="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase bg-[#faf7f2] text-[#888] border border-[#e8e0d0]">
                  {LOAN_LABELS[d.loan_type] || d.loan_type}
                </span>
              )}
            </div>
            {d.property_address && <div className="text-[10px] text-[#888] mt-0.5 truncate">{d.property_address}</div>}
          </div>
          <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold shrink-0 ${STAGE_COLORS[d.stage] || "bg-slate-100 text-slate-700"}`}>
            {d.stage?.replace(/_/g, " ")}
          </span>
        </div>
        <div className="text-lg font-bold text-[#b89d5a] mt-1">{fmtUSD(loanAmt)}</div>
      </div>

      {/* Pipeline Progress */}
      {!isDeclined && (
        <div className="px-4 pt-2 pb-1">
          <div className="flex items-center gap-0.5">
            {PIPELINE_STAGES.map((stage, i) => (
              <div key={stage} className="flex-1 flex flex-col items-center">
                <div className={`h-1 w-full rounded-full transition-all ${
                  i <= currentStageIdx ? "bg-emerald-500" : "bg-[#e8e0d0]"
                }`} />
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-0.5">
            <span className="text-[7px] text-[#bbb] uppercase">Submitted</span>
            <span className="text-[7px] text-[#bbb] uppercase">Funded</span>
          </div>
        </div>
      )}
      {isDeclined && (
        <div className="px-4 pt-2 pb-1">
          <div className="h-1 w-full rounded-full bg-red-200" />
          <div className="text-[8px] text-red-400 mt-0.5 text-center">Deal Declined</div>
        </div>
      )}

      {/* Key Metrics Row */}
      <div className="px-4 py-2">
        <div className="grid grid-cols-3 gap-2 py-2 border-t border-[#f0ebe0]">
          {ltv && (
            <div>
              <div className="text-[8px] text-[#999] uppercase">LTV</div>
              <div className="text-[11px] font-bold text-amber-700">{ltv}%</div>
            </div>
          )}
          {d.property_type && (
            <div>
              <div className="text-[8px] text-[#999] uppercase">Property</div>
              <div className="text-[10px] font-semibold text-[#333]">{d.property_type}</div>
            </div>
          )}
          {d.arv > 0 && (
            <div>
              <div className="text-[8px] text-[#999] uppercase">ARV</div>
              <div className="text-[10px] font-semibold text-emerald-700">{fmtUSD(d.arv)}</div>
            </div>
          )}
          {d.exit_strategy && (
            <div>
              <div className="text-[8px] text-[#999] uppercase">Exit</div>
              <div className="text-[10px] font-semibold text-[#333]">{d.exit_strategy}</div>
            </div>
          )}
          {d.closing_timeline_days && (
            <div>
              <div className="text-[8px] text-[#999] uppercase">Close</div>
              <div className="text-[10px] font-semibold text-[#333]">{d.closing_timeline_days}d</div>
            </div>
          )}
        </div>
      </div>

      {/* Footer: Badges + Timeline + Action */}
      <div className="px-4 pb-3 pt-0">
        <div className="flex items-center justify-between border-t border-[#f0ebe0] pt-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            {tsCount > 0 && (
              <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 border border-purple-100 font-semibold"
                data-testid={`c360-deal-ts-count-${d.lead_id}`}>
                <FileText className="h-2.5 w-2.5" /> {tsCount} term sheet{tsCount > 1 ? "s" : ""}
              </span>
            )}
            {intCount > 0 && (
              <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100 font-semibold"
                data-testid={`c360-deal-int-count-${d.lead_id}`}>
                <Users className="h-2.5 w-2.5" /> {intCount} interest{intCount > 1 ? "s" : ""}
              </span>
            )}
            {createdDate && (
              <span className="inline-flex items-center gap-1 text-[9px] text-[#aaa]"
                data-testid={`c360-deal-date-${d.lead_id}`}>
                <CalendarDays className="h-2.5 w-2.5" />
                {daysSinceCreated <= 0 ? "Today" : daysSinceCreated === 1 ? "1 day ago" : `${daysSinceCreated}d ago`}
              </span>
            )}
          </div>
          <button onClick={(e) => { e.stopPropagation(); onEntityNavigate?.("deal", d.lead_id); }}
            className="flex items-center gap-1 text-[10px] text-emerald-700 font-semibold hover:text-emerald-600 transition-colors shrink-0 hover:underline"
            data-testid={`c360-view-360-deal-${d.lead_id}`}>
            View 360 Deal <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function TermSheetCard({ ts, onViewDeal, onEntityNavigate }) {
  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 hover:border-emerald-300 cursor-pointer transition-all hover:shadow-sm group"
      onClick={() => onViewDeal?.(ts.lead_id)} data-testid={`c360-ts-${ts.term_sheet_id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-sm font-bold text-[#0A0A0A]">{fmtUSD(ts.loan_amount || ts.extra?.loan_amount || 0)}</div>
          <div className="text-[10px] text-[#888]">Deal: {ts.lead_id?.slice(0, 8)}...</div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${STAGE_COLORS[ts.status] || "bg-slate-100 text-slate-700"}`}>
          {ts.status}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-2">
        <div>
          <div className="text-[9px] text-[#888] uppercase">Rate</div>
          <div className="text-xs font-semibold text-[#333]">{ts.rate || ts.interest_rate || "—"}%</div>
        </div>
        <div>
          <div className="text-[9px] text-[#888] uppercase">LTV</div>
          <div className="text-xs font-semibold text-[#333]">{ts.ltv || "—"}%</div>
        </div>
        <div>
          <div className="text-[9px] text-[#888] uppercase">Term</div>
          <div className="text-xs font-semibold text-[#333]">{ts.term_months || "—"} mo</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-2">
        {ts.points && (
          <div>
            <div className="text-[9px] text-[#888] uppercase">Points</div>
            <div className="text-xs font-semibold text-[#333]">{ts.points}</div>
          </div>
        )}
        {ts.closing_days && (
          <div>
            <div className="text-[9px] text-[#888] uppercase">Close</div>
            <div className="text-xs font-semibold text-[#333]">{ts.closing_days} days</div>
          </div>
        )}
        {(ts.prepayment_penalty || ts.prepayment_penalty_structure) && (
          <div>
            <div className="text-[9px] text-[#888] uppercase">Prepay Penalty</div>
            <div className="text-xs font-semibold text-[#333]">{ts.prepayment_penalty || "None"}</div>
          </div>
        )}
      </div>

      {ts.fees && (
        <div className="mt-2 text-[10px] text-[#888]">Fees: {ts.fees}</div>
      )}
      {ts.conditions && (
        <div className="mt-1 text-[10px] text-amber-600">Conditions: {ts.conditions}</div>
      )}

      {/* View 360 Deal link */}
      <div className="flex justify-end mt-2 pt-2 border-t border-[#f0ebe0]">
        <button onClick={(e) => { e.stopPropagation(); onEntityNavigate?.("deal", ts.lead_id); }}
          className="flex items-center gap-1 text-[10px] text-emerald-700 font-semibold hover:text-emerald-600 transition-colors hover:underline"
          data-testid={`c360-ts-view-360-${ts.term_sheet_id}`}>
          View 360 Deal <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function CommitmentCard({ c, onViewDeal, onEntityNavigate }) {
  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 hover:border-emerald-300 cursor-pointer transition-all hover:shadow-sm group"
      onClick={() => onViewDeal?.(c.lead_id)} data-testid={`c360-commitment-${c.commitment_id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-[#0A0A0A]">{fmtUSD(c.amount)}</div>
          <div className="text-[10px] text-[#888]">{c.loan_type || c.fund_name || "Investment"}{c.state ? ` — ${c.state}` : ""}</div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
          c.status === "funded" ? "bg-green-100 text-green-700" :
          c.status === "committed" ? "bg-blue-100 text-blue-700" :
          "bg-amber-100 text-amber-700"
        }`}>{c.status}</span>
      </div>
      {c.property_address && <div className="text-[10px] text-[#999] mt-1">{c.property_address}</div>}
      {/* Footer: Date + View 360 Deal */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#f0ebe0]">
        {c.created_at && <div className="text-[10px] text-[#bbb]">{new Date(c.created_at).toLocaleDateString()}</div>}
        {c.lead_id && (
          <button onClick={(e) => { e.stopPropagation(); onEntityNavigate?.("deal", c.lead_id); }}
            className="flex items-center gap-1 text-[10px] text-emerald-700 font-semibold hover:text-emerald-600 transition-colors hover:underline"
            data-testid={`c360-commitment-view-360-${c.commitment_id}`}>
            View 360 Deal <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, color = "text-[#0A0A0A]" }) {
  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-3 text-center">
      <div className={`text-lg font-bold ${color}`}>{value}</div>
      <div className="text-[9px] text-[#999] uppercase">{label}</div>
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="text-center py-12 text-xs text-[#999]">{text}</div>;
}


function EmailHistory({ userId, headers, refreshKey }) {
  const [emails, setEmails] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  React.useEffect(() => {
    if (!userId) return;
    setLoading(true);
    axios.get(`${API}/ai-success/user/${userId}/history`, { headers })
      .then(r => setEmails(r.data.sent || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId, refreshKey, API, headers]);

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-[#ccc]" /></div>;
  if (emails.length === 0) return null;

  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid="c360-email-history">
      <h4 className="text-xs font-bold uppercase tracking-wider text-[#999] mb-3 flex items-center gap-2">
        <Mail className="h-3.5 w-3.5" /> Sent History ({emails.length})
      </h4>
      <div className="space-y-2 max-h-[200px] overflow-y-auto">
        {emails.map((e, i) => (
          <div key={e.draft_id || i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#faf7f2] border border-[#e8e0d0]">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-[#333] truncate">{e.subject}</div>
              <div className="text-[10px] text-[#999]">
                {e.context_type?.replace(/_/g, " ")} — {e.sent_at ? new Date(e.sent_at).toLocaleString() : e.created_at ? new Date(e.created_at).toLocaleString() : ""}
              </div>
            </div>
            <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 ml-2" />
          </div>
        ))}
      </div>
    </div>
  );
}
